/**
 * Supabase Storage helper for uploading test patterns (mires)
 */

import { supabase } from './supabaseClient';

const BUCKET_NAME = 'VisualGrid_Bucket';

/**
 * Upload a test pattern image to Supabase Storage
 * @param dataUrl - The base64 data URL of the image
 * @param filename - The filename to use for the upload
 * @returns Object with success status, public URL, and error if any
 */
export async function uploadTestPatternToSupabase(
  dataUrl: string,
  filename: string
): Promise<{ success: boolean; publicUrl?: string; error?: string }> {
  try {
    // Convert data URL to Blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    
    // Create a unique path with user display name if authenticated
    const { data: { user } } = await supabase.auth.getUser();
    let userIdentifier = 'anonymous';
    
    if (user?.id) {
      // Try to get the user's display name from the profiles table
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user.id)
        .single();
      
      // Use display_name if available, otherwise fall back to email or UUID
      if (profile?.display_name) {
        userIdentifier = profile.display_name.replace(/[^a-zA-Z0-9-_]/g, '_');
      } else if (user.email) {
        userIdentifier = user.email.split('@')[0].replace(/[^a-zA-Z0-9-_]/g, '_');
      } else {
        userIdentifier = user.id;
      }
    }
    
    const path = `test-patterns/${userIdentifier}/${filename}`;
    
    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(path, blob, {
        contentType: 'image/png',
        upsert: true,
      });

    if (error) {
      console.error('Supabase Storage upload error:', error);
      return { success: false, error: error.message };
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(path);

    return { success: true, publicUrl };
  } catch (error) {
    console.error('Error uploading test pattern:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Delete a test pattern from Supabase Storage
 * @param filename - The filename to delete
 * @param userId - Optional user ID (defaults to current authenticated user)
 */
export async function deleteTestPatternFromSupabase(
  filename: string,
  userId?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const effectiveUserId = userId || user?.id || 'anonymous';
    const path = `test-patterns/${effectiveUserId}/${filename}`;
    
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([path]);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting test pattern:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * List all test patterns for the current user
 * @returns Array of test pattern metadata
 */
export async function listUserTestPatterns(): Promise<{
  success: boolean;
  patterns?: Array<{ name: string; url: string; created_at: string }>;
  error?: string;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    const path = `test-patterns/${user.id}`;
    
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .list(path);

    if (error) {
      return { success: false, error: error.message };
    }

    const patterns = data?.map(item => ({
      name: item.name,
      url: `${path}/${item.name}`,
      created_at: item.created_at,
    })) || [];

    return { success: true, patterns };
  } catch (error) {
    console.error('Error listing test patterns:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}
